import React, { useState, useEffect } from 'react';

import { Zap, Plus, Copy, Check, Trash2, Key, Activity, Code, Eye, EyeOff, Clock } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';
const getToken = () => localStorage.getItem('accessToken') || localStorage.getItem('token');

interface APIKey {
  id: string;
  _id?: string;
  name: string;
  key: string;
  preview?: string;
  lastUsed?: string;
  callCount?: number;
  createdAt?: string;
  expiresAt?: string;
}

export default function ApiTab() {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  const fetchAPIKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required'); return; }
      const res = await fetch(`${API_URL}/keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setApiKeys(data.keys || data.data || []);
      else setError(data.error || 'Failed to load API keys');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAPIKeys(); }, []);

  const handleGenerateAPIKey = async () => {
    setGeneratingKey(true);
    setError('');
    const keyName = window.prompt('Enter a name for this API key:', 'Production Key');
    if (!keyName) { setGeneratingKey(false); return; }
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/keys/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: keyName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        const newKey = data.key || data.data;
        setApiKeys((prev) => [newKey, ...prev]);
        if (newKey?.key) {
          navigator.clipboard?.writeText(newKey.key);
          setCopiedKeyId(newKey.id || newKey._id || '');
          setSuccessMsg('API Key generated and copied to clipboard!');
          setTimeout(() => { setCopiedKeyId(''); setSuccessMsg(''); }, 3000);
        }
      } else {
        setError(data.error || 'Failed to generate API key');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!window.confirm(`Permanently revoke "${keyName}"? This cannot be undone.`)) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/keys/${keyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setApiKeys((prev) => prev.filter((k) => (k.id || k._id) !== keyId));
        setSuccessMsg(`Key "${keyName}" revoked successfully`);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        setError(data.error || 'Failed to revoke key');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to revoke key');
    }
  };

  const handleCopyKey = (key: APIKey) => {
    navigator.clipboard?.writeText(key.key);
    const keyId = key.id || key._id || '';
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(''), 2000);
  };

  const toggleKeyVisibility = (keyId: string) => {
    setShowKey((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskKey = (key: string): string => {
    if (!key) return '••••••••••••••••';
    return key.substring(0, 8) + '••••••••••••••••' + key.substring(key.length - 4);
  };

  const totalAPICalls = apiKeys.reduce((sum, k) => sum + (k.callCount || 0), 0);
  const activeKeys = apiKeys.filter(k => !k.expiresAt || new Date(k.expiresAt) > new Date()).length;

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try { return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return 'Invalid date'; }
  };

  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return formatDate(dateString);
    } catch { return 'Unknown'; }
  };

  return (
    <div>
      {/* Header */}
      <div className="api-header">
        <div>
          <h3 className="api-title">
            <Zap size={20} color="#7c3aed" />
            API Access
          </h3>
          <p className="api-subtitle">Manage API keys for programmatic access to your account</p>
        </div>
        <button onClick={handleGenerateAPIKey} disabled={generatingKey} className="api-generate-btn">
          <Plus size={18} />
          {generatingKey ? 'Generating...' : 'Generate New Key'}
        </button>
      </div>

      {/* Messages */}
      {successMsg && <div className="api-msg-success"><Check size={16} /> {successMsg}</div>}
      {error && <div className="api-msg-error">❌ {error} <button onClick={() => setError('')} className="api-msg-close">×</button></div>}

      {/* Stats */}
      <div className="api-stats-grid">
        <div className="api-stat-card" style={{ background: '#faf5ff', border: '1px solid #e9d5ff' }}>
          <div className="api-stat-value" style={{ color: '#7c3aed' }}>{activeKeys}</div>
          <div className="api-stat-label">Active Keys</div>
        </div>
        <div className="api-stat-card" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
          <div className="api-stat-value" style={{ color: '#16a34a' }}>{totalAPICalls.toLocaleString()}</div>
          <div className="api-stat-label">Total API Calls</div>
        </div>
        <div className="api-stat-card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <div className="api-stat-value" style={{ color: '#2563eb' }}>100%</div>
          <div className="api-stat-label">Uptime SLA</div>
        </div>
        <div className="api-stat-card" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
          <div className="api-stat-value" style={{ color: '#d97706' }}>v1</div>
          <div className="api-stat-label">API Version</div>
        </div>
      </div>

      {/* Docs Link */}
      <div className="api-docs-banner">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Code size={18} color="#7c3aed" />
          <div>
            <strong style={{ fontSize: '14px' }}>API Documentation</strong>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>
              Base URL: <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{API_URL}/api</code>
            </p>
          </div>
        </div>
        <a href={`${API_URL}/docs`} target="_blank" rel="noopener noreferrer" className="api-docs-link">
          View Docs <Code size={14} />
        </a>
      </div>

      {/* Keys List */}
      <div className="api-keys-container">
        <h4 className="api-keys-title"><Key size={16} /> Your API Keys {apiKeys.length > 0 && <span className="api-keys-count">({apiKeys.length} total)</span>}</h4>

        {loading ? (
          <div className="api-loading"><Activity size={24} /><p>Loading API keys...</p></div>
        ) : apiKeys.length === 0 ? (
          <div className="api-empty">
            <Key size={40} />
            <p style={{ fontSize: '15px', marginBottom: '8px', color: '#6b7280' }}>No API keys yet</p>
            <p style={{ fontSize: '13px', marginBottom: '16px' }}>Generate your first API key to start using the API</p>
            <button onClick={handleGenerateAPIKey} className="api-empty-btn"><Plus size={16} /> Generate API Key</button>
          </div>
        ) : (
          <div className="api-keys-list">
            {apiKeys.map((key) => {
              const keyId = key.id || key._id || '';
              const isVisible = showKey[keyId] || false;
              return (
                <div key={keyId} className="api-key-card">
                  <div className="api-key-info">
                    <div className="api-key-name"><Zap size={16} color="#7c3aed" /><strong>{key.name || 'Production Key'}</strong></div>
                    <div className="api-key-value">
                      <code>{isVisible ? key.key : maskKey(key.key)}</code>
                      <button onClick={() => toggleKeyVisibility(keyId)} className="api-key-toggle" title={isVisible ? 'Hide key' : 'Show key'}>
                        {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <div className="api-key-meta">
                      <span><Clock size={12} /> Created {formatDate(key.createdAt)}</span>
                      <span><Activity size={12} /> {key.callCount || 0} calls</span>
                      <span>Last used: {formatRelativeTime(key.lastUsed)}</span>
                    </div>
                  </div>
                  <div className="api-key-actions">
                    <button onClick={() => handleCopyKey(key)} className="api-key-copy-btn">
                      {copiedKeyId === keyId ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                    </button>
                    <button onClick={() => handleRevokeKey(keyId, key.name || 'Key')} className="api-key-revoke-btn">
                      <Trash2 size={14} /> Revoke
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
} 